import { useRef, useState } from 'react'
import './ContactPage.css'

const API = process.env.REACT_APP_API_URL || "";

const ISSUE_TYPES = [
    { value: '', label: 'Select issue type...' },
    { value: 'hardware-repair', label: 'Hardware Repair' },
    { value: 'firmware', label: 'Firmware Issue' },
    { value: 'spare-parts', label: 'Spare Parts Request' },
    { value: 'technical', label: 'Technical Troubleshooting' },
    { value: 'other', label: 'Other' },
]

const FIELDS = [
    { id: 'name', label: 'Full Name', type: 'text', span: 1 },
    { id: 'phone', label: 'Phone Number', type: 'tel', span: 1 },
    { id: 'email', label: 'Email Address', type: 'email', span: 2 },
    { id: 'productModel', label: 'Product / Drone Model', type: 'text', span: 2 },
    { id: 'issueType', label: 'Issue Type', type: 'select', options: ISSUE_TYPES, span: 2 },
    { id: 'message', label: 'Describe Your Issue', type: 'area', span: 2 },
]

export default function SupportPage() {
    const formRef = useRef(null)
    const [focused, setFocused] = useState(null)
    const [values, setValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [generalError, setGeneralError] = useState(null)
    const [sent, setSent] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const handleChange = (id, val) => {
        setValues(p => ({ ...p, [id]: val }))
        if (fieldErrors[id]) setFieldErrors(p => { const n = { ...p }; delete n[id]; return n })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setGeneralError(null)
        try {
            const res = await fetch(`${API}/api/support`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values),
            })
            const data = await res.json()
            if (!res.ok) {
                if (data.errors) setFieldErrors(data.errors)
                else setGeneralError(data.message || 'Submission failed.')
                return
            }
            setSent(true)
            setValues({})
        } catch {
            setGeneralError('Connection error. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="cp-wrap">
            {/* Hero */}
            <div className="cp-hero">
                <div className="cp-hero-grid" />
                <div className="cp-hero-inner">
                    <div className="cp-hero-eyebrow">
                        <span className="cp-hero-dot" style={{ background: '#0ea5e9' }} />
                        AVIATRICKS · HELP & SUPPORT
                    </div>
                    <h1 className="cp-hero-title">
                        Help &<br />
                        <em style={{ WebkitTextStrokeColor: '#0ea5e9' }}>Support.</em>
                    </h1>
                    <p className="cp-hero-sub">
                        Having an issue with your drone or product? Tell us what's wrong
                        and our technical team will get back to you within 24–48 hours.
                    </p>
                </div>
            </div>

            {/* Body */}
            <div className="cp-body">
                {/* Left info */}
                <div className="cp-info">
                    <div className="cp-info-label">What we help with</div>
                    <div className="cp-info-items">
                        {['Hardware Repair', 'Firmware Updates', 'Warranty Claims', 'Spare Parts', 'Technical Issues'].map(item => (
                            <div key={item} className="cp-info-item">
                                <span className="cp-info-item-val">→ {item}</span>
                            </div>
                        ))}
                        <div className="cp-info-item">
                            <span className="cp-info-item-label">Response Time</span>
                            <span className="cp-info-item-val">24 – 48 hours</span>
                        </div>
                        <div className="cp-info-item">
                            <span className="cp-info-item-label">Email</span>
                            <span className="cp-info-item-val">support@aviatricks.in</span>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div>
                    {!sent ? (
                        <form ref={formRef} className="cp-form" onSubmit={handleSubmit} noValidate>
                            {generalError && <div className="cp-general-error">{generalError}</div>}
                            <div className="cp-grid">
                                {FIELDS.map(f => (
                                    <div key={f.id}
                                        className={`cp-field cp-field--span${f.span} ${focused === f.id || values[f.id] ? 'cp-field--active' : ''}`}
                                    >
                                        <label className="cp-label" htmlFor={f.id}>{f.label}</label>
                                        {f.type === 'area' ? (
                                            <textarea id={f.id} rows={4} className="cp-input" placeholder={f.placeholder}
                                                value={values[f.id] || ''} onFocus={() => setFocused(f.id)} onBlur={() => setFocused(null)}
                                                onChange={e => handleChange(f.id, e.target.value)} />
                                        ) : f.type === 'select' ? (
                                            <select id={f.id} className="cp-select"
                                                value={values[f.id] || ''} onFocus={() => setFocused(f.id)} onBlur={() => setFocused(null)}
                                                onChange={e => handleChange(f.id, e.target.value)}>
                                                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        ) : (
                                            <input id={f.id} type={f.type} className="cp-input" placeholder={f.placeholder}
                                                value={values[f.id] || ''} onFocus={() => setFocused(f.id)} onBlur={() => setFocused(null)}
                                                onChange={e => handleChange(f.id, e.target.value)} />
                                        )}
                                        {fieldErrors[f.id] && <span className="cp-field-error">{fieldErrors[f.id]}</span>}
                                        <div className="cp-field-line" />
                                    </div>
                                ))}
                            </div>
                            <button type="submit" className="cp-submit" disabled={submitting}>
                                <span>{submitting ? 'Submitting...' : 'Submit Request'}</span>
                                {!submitting && <span className="cp-submit-arrow">→</span>}
                            </button>
                        </form>
                    ) : (
                        <div className="cp-success">
                            <div className="cp-success-icon">✓</div>
                            <h3>Request Sent.</h3>
                            <p>Check your inbox — we've sent a confirmation email.</p>
                            <p>Our team will get back to you within 24–48 hours.</p>
                            <button onClick={() => setSent(false)} className="cp-retry">Submit Another</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}